/**
 * Requisiti d'impianto delle Regole Operative CACER, condivisi fra il form del
 * profilo di produzione (apps/web) e la scrittura su `production_plants` (API).
 *
 * Fino a INB-322 (T-05) le costanti esistevano ma le usava solo il simulatore:
 * un impianto da 1.500 kWp, in esercizio dal 2020 e con il 50% di PNRR si
 * salvava senza una parola, e una data di esercizio mancante diventava «oggi»,
 * cioè un impianto nuovo.
 *
 * Due livelli, perché le RO distinguono fra ciò che non ha senso e ciò che è
 * ammesso ma senza tariffa:
 * - `errore`: il dato non si salva (data assente o non valida, potenza ≤ 0,
 *   contributo fuori da 0–100%);
 * - `avviso`: il dato è legittimo, ma l'impianto non accede alla tariffa premio
 *   o fa sforare un limite. Un impianto esistente in una CER è ammesso — conta
 *   nel 30% e riceve solo la valorizzazione — quindi rifiutarlo sarebbe sbagliato.
 */

import {
	ELIGIBILITY_DATE,
	MAX_CUMULATIVE_GRANT_PCT,
	MAX_PLANT_KW,
	MAX_PRE_EXISTING_RATIO,
} from '../constants/gse-tariffs';

export type PlantRequirementField = 'potenzaKwp' | 'dataEsercizio' | 'pnrrGrantPct';

export interface PlantRequirementIssue {
	campo: PlantRequirementField;
	livello: 'errore' | 'avviso';
	messaggio: string;
}

/** `YYYY-MM-DD` del taglio del 16/12/2021 (RO §1.2.1.2 lett. c). */
export const DATA_ESERCIZIO_MINIMA_TARIFFA = ELIGIBILITY_DATE.toISOString().slice(0, 10);

export const DATA_ESERCIZIO_REQUIRED_ERROR =
	"Data di entrata in esercizio obbligatoria: senza, non si sa se l'impianto accede alla tariffa premio o conta fra gli esistenti.";

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function isValidIsoDate(value: string): boolean {
	if (!ISO_DATE.test(value)) return false;
	const d = new Date(`${value}T00:00:00Z`);
	return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}

/**
 * Controlla i campi d'impianto presenti in `plant`. Un campo `undefined` non si
 * controlla (una PATCH che non lo tocca); `null` o stringa vuota sulla data sì,
 * perché vuol dire cancellarla.
 */
export function checkPlantRequirements(plant: {
	potenzaKwp?: number | null;
	dataEsercizio?: string | null;
	pnrrGrantPct?: number | null;
}): PlantRequirementIssue[] {
	const issues: PlantRequirementIssue[] = [];

	if (plant.potenzaKwp !== undefined) {
		const kw = plant.potenzaKwp;
		if (kw == null || !Number.isFinite(kw) || kw <= 0)
			issues.push({
				campo: 'potenzaKwp',
				livello: 'errore',
				messaggio: 'La potenza deve essere maggiore di 0 kWp.',
			});
		else if (kw > MAX_PLANT_KW)
			issues.push({
				campo: 'potenzaKwp',
				livello: 'avviso',
				messaggio: `Oltre ${MAX_PLANT_KW.toLocaleString('it-IT')} kW l'impianto non accede alla tariffa premio (RO §1.2.1.2, lett. b).`,
			});
	}

	if (plant.dataEsercizio !== undefined) {
		const data = (plant.dataEsercizio ?? '').trim();
		if (!data)
			issues.push({
				campo: 'dataEsercizio',
				livello: 'errore',
				messaggio: DATA_ESERCIZIO_REQUIRED_ERROR,
			});
		else if (!isValidIsoDate(data))
			issues.push({
				campo: 'dataEsercizio',
				livello: 'errore',
				messaggio: 'Data di entrata in esercizio non valida.',
			});
		else if (data < DATA_ESERCIZIO_MINIMA_TARIFFA)
			issues.push({
				campo: 'dataEsercizio',
				livello: 'avviso',
				messaggio:
					'In esercizio prima del 16/12/2021: impianto esistente, senza tariffa premio. Conta nel limite del 30% della potenza della CER (RO §1.2.1.2).',
			});
	}

	if (plant.pnrrGrantPct !== undefined && plant.pnrrGrantPct != null) {
		const pct = plant.pnrrGrantPct;
		if (!Number.isFinite(pct) || pct < 0 || pct > 100)
			issues.push({
				campo: 'pnrrGrantPct',
				livello: 'errore',
				messaggio: 'Il contributo in conto capitale va da 0 a 100%.',
			});
		else if (pct > MAX_CUMULATIVE_GRANT_PCT * 100)
			issues.push({
				campo: 'pnrrGrantPct',
				livello: 'avviso',
				messaggio: `Oltre il ${MAX_CUMULATIVE_GRANT_PCT * 100}% il contributo non è cumulabile con la tariffa premio (RO Appendice B §3).`,
			});
	}

	return issues;
}

/**
 * Quota di potenza degli impianti esistenti su una CER (RO §1.2.1.2): oltre il
 * 30% del totale la configurazione non rispetta il requisito. Restituisce il
 * messaggio d'avviso, o `undefined` se la quota è entro il limite.
 */
export function checkPreExistingShare(
	plants: readonly { potenzaKwp: number | null; dataEsercizio: string | null }[],
): { quotaPct: number; messaggio: string } | undefined {
	const totale = plants.reduce((s, p) => s + (Number(p.potenzaKwp) || 0), 0);
	if (totale <= 0) return undefined;
	const esistenti = plants
		.filter((p) => !!p.dataEsercizio && p.dataEsercizio < DATA_ESERCIZIO_MINIMA_TARIFFA)
		.reduce((s, p) => s + (Number(p.potenzaKwp) || 0), 0);
	const quota = esistenti / totale;
	if (quota <= MAX_PRE_EXISTING_RATIO) return undefined;
	const quotaPct = Math.round(quota * 1000) / 10;
	return {
		quotaPct,
		messaggio: `Gli impianti esistenti sono il ${quotaPct.toLocaleString('it-IT')}% della potenza della configurazione: il limite è ${MAX_PRE_EXISTING_RATIO * 100}% (RO §1.2.1.2).`,
	};
}
